import { useEffect, useMemo, useState } from 'react';
import type { ActivateFunction } from '@elfsight/widget-utils';
import {
  documentScrollOver,
  exitIntent,
  onPageLoadTrigger,
  timeOnPageOver
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useFirstVisitStorage, useRememberVisitorStorage } from '../../helpers';

const TIME_ON_PAGE = 'timeOnPage';
const TIME_ON_SITE = 'timeOnSite';
const SCROLL_POSITION = 'scrollPosition';
const ON_PAGE_LOAD = 'onPageLoad';
const EXIT_INTENT = 'exitIntent';

export const useTriggers = () => {
  const {
    editMode,
    timeOnPage,
    timeOnSite,
    scrollPosition,
    exitIntent: exitIntentSetting,
    onPageLoad,
    position
  } = useSettings();

  const [
    rememberVisitorStorageValue,
    { get: getStorageRememberVisitor, set: setStorageRememberVisitor }
  ] = useRememberVisitorStorage();
  const [, { get: getStorageFirstVisit, set: setStorageFirstVisit }] =
    useFirstVisitStorage();
  const dateOnSeconds = Math.floor(Date.now() / 1000);
  const firstVisit = getStorageFirstVisit() || dateOnSeconds;
  const [firstVisitState, setFirstVisitState] = useState<number | null>(
    firstVisit
  );

  const [
    triggerTimeOnPage,
    triggerTimeOnSite,
    triggerScrollPosition,
    triggerExitIntent,
    triggerOnPageLoad
  ] = useMemo(
    () => [
      Number(timeOnPage),
      Number(timeOnSite),
      Number(scrollPosition),
      Boolean(exitIntentSetting),
      Boolean(onPageLoad)
    ],
    [exitIntentSetting, onPageLoad, scrollPosition, timeOnPage, timeOnSite]
  );

  useEffect(() => {
    const rememberVisitor = getStorageRememberVisitor();
    if (rememberVisitor && rememberVisitor < dateOnSeconds) {
      setStorageRememberVisitor(null);
    }
  }, [dateOnSeconds]);

  useEffect(() => {
    const rememberVisitor = getStorageRememberVisitor();
    const firstVisit = getStorageFirstVisit();
    if (!triggerTimeOnSite) return;

    const isFirstVisit =
      (!!firstVisit && firstVisit + triggerTimeOnSite < dateOnSeconds) ||
      (!firstVisit && !rememberVisitor);

    if (isFirstVisit) {
      setStorageFirstVisit(dateOnSeconds);
      setFirstVisitState(dateOnSeconds);
    }
  }, [dateOnSeconds, triggerTimeOnSite, rememberVisitorStorageValue]);

  const duration = useMemo(() => {
    const startTime = dateOnSeconds;
    const endTime = (firstVisitState || 0) + triggerTimeOnSite;

    return Math.max(endTime - startTime, 0);
  }, [dateOnSeconds, firstVisitState, triggerTimeOnSite]);

  return useMemo(() => {
    const rememberVisitor = getStorageRememberVisitor();
    if (editMode || rememberVisitor || position === 'embed-chat') return null;

    return [
      triggerOnPageLoad && onPageLoadTrigger(ON_PAGE_LOAD),
      triggerScrollPosition > 0 &&
        documentScrollOver(SCROLL_POSITION, triggerScrollPosition / 100),
      triggerTimeOnPage > 0 && timeOnPageOver(TIME_ON_PAGE, triggerTimeOnPage),
      triggerTimeOnSite > 0 &&
        duration >= 0 &&
        timeOnPageOver(TIME_ON_SITE, duration),
      triggerExitIntent && exitIntent(EXIT_INTENT)
    ].filter(Boolean) as ActivateFunction[];
  }, [
    editMode,
    position,
    triggerOnPageLoad,
    triggerScrollPosition,
    triggerTimeOnPage,
    triggerTimeOnSite,
    duration,
    triggerExitIntent
  ]);
};
